import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  FileText,
  Upload,
  Download,
  Filter,
  Trash2,
  Loader2,
  Layers,
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  Eye,
  Paperclip,
  Files,
  ShieldCheck,
  Lock,
  ScrollText,
  UserX,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getRoleLabel } from '../../lib/utils';
import { documentsApi } from '../../lib/api';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';

// (iter102) Top "Signed Documents" collection removed entirely. Signed PDFs are reached
// via the small clickable chip next to the source version row inside each per-type
// collection. `SIGNED_GROUP_KEY` and `sourceTypeFromSignedDocType` were retired.
// A bundle = `is_ft_bundle === true` OR doc_type endsWith '_Signature_Bundle' OR
//            doc_type === 'Bundle' (manual non-final bundles)
const isBundleDocType = (dt) =>
  !!dt && (dt.endsWith('_Signature_Bundle') || dt === 'Bundle' || dt.startsWith('Signed_') && dt.endsWith('_Signature_Bundle'));
const isBundleDoc = (d) =>
  d?.is_ft_bundle === true || (d?.doc_type || '').endsWith('_Signature_Bundle');
// A "signed" doc has a Signed_ prefix on doc_type OR filename. Bundles that have been
// e-signed get doc_type 'Signed_*_Signature_Bundle' so they count as signed.
const isSignedDoc = (d) => {
  const dt = d?.doc_type || '';
  const fn = d?.filename || '';
  return dt.startsWith('Signed_') || fn.startsWith('Signed_');
};
const isSignedBundle = (d) => isSignedDoc(d) && (d?.doc_type || '').endsWith('_Signature_Bundle');
// Unsigned, non-final bundles (the "Generate Bundle PDF" tool output, OR the staging
// FT_Signature_Bundle before chairman signs it). These should NEVER show in the tab.
const isUnsignedStagingBundle = (d) => {
  const dt = d?.doc_type || '';
  if (dt === 'Bundle') return true;                          // manual generated bundles
  if (d?.is_ft_bundle === true && !isSignedDoc(d)) return true; // pre-sign FT bundle
  return false;
};

// Format "Uploader · Role" for the meta line under a doc/version/attachment row.
// Falls back gracefully if either piece is missing.
const formatUploaderMeta = (uploader_name, uploader_role) => {
  const name = (uploader_name || '').trim();
  const role = uploader_role ? getRoleLabel(uploader_role) : '';
  if (name && role) return `${name} · ${role}`;
  return name || role || 'Unknown';
};

// Source doc_type extraction for a signed *individual* doc — retired in iter102 since
// signed PDFs are no longer surfaced as their own collection items.

export function DocumentsTab({
  requestId,
  documents,
  setDocuments,
  availableDocTypes,
  canUploadDocs,
  setShowUploadModal,
  handleDownloadDocument,
  downloadingDoc,
  isAdmin = false,
  bundleToolAvailable = false,
  onOpenBundleDialog = null,
  onGoToThread = null,
  onCountChange = null,
}) {
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [openItems, setOpenItems] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);

  const handleFilterChange = async (value) => {
    setDocTypeFilter(value);
    try {
      const params = value !== 'all' ? { doc_type: value } : {};
      const res = await documentsApi.getAll(requestId, params);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to filter documents:', error);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!docToDelete) return;
    setDeletingDocId(docToDelete.id);
    try {
      await documentsApi.adminDelete(requestId, docToDelete.id);
      toast.success(`"${docToDelete.filename}" and its discussion thread were deleted`);
      setDocuments((prev) => prev.filter((d) => d.id !== docToDelete.id));
      setDocToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingDocId(null);
    }
  };

  const groups = useMemo(() => buildGroups(documents), [documents]);
  // iter112 — total = every "visible row" across all collections AND bundle chains:
  // head + each older version + each attachment. Matches what the user sees when
  // every collection is expanded.
  const totalCount = useMemo(() => (
    groups.reduce((acc, g) => {
      if (g.key === '__bundles__') {
        // bundle chains: each chain contributes versionCount + attachmentCount rows
        const chains = g.items || [];
        return acc + chains.reduce((s, c) => s + (c.versionCount || 1) + (c.attachmentCount || 0), 0);
      }
      // regular doc-type collection: head + extra versions + attachments
      const items = g.items || [];
      const versions = items.reduce((s, it) => s + 1 + (it.versions?.length || 0), 0);
      const atts = g._counts?.attachments || 0;
      return acc + versions + atts;
    }, 0)
  ), [groups]);
  // iter112 — bubble the visible-rows count up so the RequestDetails tab badge can
  // render the SAME number as the Documents-tab header (Documents (N)).
  useEffect(() => {
    if (typeof onCountChange === 'function') onCountChange(totalCount);
  }, [totalCount, onCountChange]);
  // Filter dropdown excludes bundle / signature-bundle types and Signed_*
  const filterableDocTypes = useMemo(() => (
    (availableDocTypes || []).filter((t) => (
      !(t || '').startsWith('Signed_') &&
      !(t || '').endsWith('_Signature_Bundle') &&
      t !== 'Bundle'
    ))
  ), [availableDocTypes]);
  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));
  const toggleItem = (key) => setOpenItems((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle data-testid="documents-tab-title">Documents ({totalCount})</CardTitle>
          <div className="flex items-center gap-3">
            {filterableDocTypes.length > 0 && (
              <Select value={docTypeFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-[160px] h-9" data-testid="doc-type-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-500" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {filterableDocTypes.map((docType) => (
                    <SelectItem key={docType} value={docType}>{docType}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canUploadDocs && (
              <Button
                onClick={() => setShowUploadModal(true)}
                data-testid="upload-doc-btn"
                className="bg-blue-900 hover:bg-blue-950 gap-2"
              >
                <Upload size={16} />
                Upload Document
              </Button>
            )}
            {bundleToolAvailable && documents.filter((d) => !d.is_thread_attachment).length >= 2 && (
              <Button
                onClick={() => onOpenBundleDialog?.()}
                data-testid="generate-bundle-btn"
                className="relative gap-2 font-medium text-white shadow-sm bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 hover:from-slate-800 hover:via-indigo-800 hover:to-blue-800 ring-1 ring-indigo-300/40 hover:ring-indigo-300/70 transition-all"
                title="Combine multiple documents into one PDF"
              >
                <Layers size={16} />
                Generate Bundle Doc
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* iter166c — Viewer (read-only) banner. When every visible doc was
              granted to the current user via the request's `viewer_user_ids`
              (i.e. `viewer_only=true` on every row), surface a single banner
              explaining the access model — much better than a per-doc badge
              that would repeat for every row. */}
          {documents.length > 0 && documents.every((d) => d.viewer_only) && (
            <div
              className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900"
              data-testid="documents-viewer-only-banner"
            >
              <Eye className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600" />
              <div className="text-sm leading-relaxed">
                <span className="font-semibold">View only access.</span> You can see every document
                here because you were granted read-only viewer access for this request — even though
                you&apos;re not assigned to a specific doc thread. You can&apos;t upload, comment, or
                sign unless you&apos;re added to a thread.
              </div>
            </div>
          )}
          {documents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No documents uploaded yet</div>
          ) : (
            <div className="space-y-3" data-testid="doc-groups">
              {groups.map((g) => (
                g.key === '__bundles__' ? (
                  <BundleHero
                    key={g.key}
                    group={g}
                    documentsById={Object.fromEntries(documents.map((d) => [d.id, d]))}
                    onPreview={setPreviewDoc}
                    onDownload={handleDownloadDocument}
                    downloadingDoc={downloadingDoc}
                    isAdmin={isAdmin}
                    onDeleteClick={setDocToDelete}
                    deletingDocId={deletingDocId}
                  />
                ) : (
                  <DocCollection
                    key={g.key}
                    group={g}
                    isOpen={!!openGroups[g.key]}
                    onToggle={() => toggleGroup(g.key)}
                    openItems={openItems}
                    onItemToggle={toggleItem}
                    onPreview={setPreviewDoc}
                    onDownload={handleDownloadDocument}
                    downloadingDoc={downloadingDoc}
                    isAdmin={isAdmin}
                    onDeleteClick={setDocToDelete}
                    deletingDocId={deletingDocId}
                  />
                )
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter167 Issue 36 — "Your files are safe with us" reassurance box.
          Renders as a separate bordered card directly below the Documents
          card so Company Reps and Investigators can see, at a glance, why
          it is safe to upload sensitive clinical documents (protocols,
          budgets, signed CTAs) to ClinicalFlow. */}
      <Card
        className="mt-4 border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 shadow-sm"
        data-testid="documents-trust-box"
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-emerald-900 mb-3 font-['Manrope']">
                Your files are safe with us
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Secure storage</span>
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>End-to-end encryption</span>
                </li>
                <li className="flex items-center gap-2">
                  <ScrollText className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Compliant with data protection regulations</span>
                </li>
                <li className="flex items-center gap-2">
                  <UserX className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Not accessible by unauthorized users</span>
                </li>
                <li className="flex items-center gap-2 sm:col-span-2">
                  <EyeOff className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Not accessible or shareable by the ClinicalFlow platform</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null); }}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{docToDelete?.filename}</strong> and its entire
              discussion thread (including replies, attachments, and signature records for this
              document). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-doc-btn" disabled={!!deletingDocId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteConfirmed(); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-doc-btn"
              disabled={!!deletingDocId}
            >
              {deletingDocId ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Delete permanently</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentPreviewModal
        open={!!previewDoc}
        onOpenChange={(open) => { if (!open) setPreviewDoc(null); }}
        doc={previewDoc}
        requestId={requestId}
        /* Documents tab preview never offers Nudge — that is gate-only */
        onGoToThread={(threadId) => {
          setPreviewDoc(null);
          onGoToThread?.(threadId, 'doc-preview');
        }}
      />
    </>
  );
}

/**
 * Build collections.
 *
 *   Hidden from the tab entirely:
 *     • Unsigned staging bundles (is_ft_bundle && !Signed_, OR doc_type === 'Bundle')
 *     • Any bundle-typed doc as a top-level doc_type collection
 *
 *   1. **Signed** (top): every signed doc (Signed_*) + every signed bundle.
 *        — Bundles carry a "Bundle" chip.
 *        — Each signed entry is expandable to reveal the source version(s) +
 *          per-version attachments.
 *
 *   2. **Per source doc_type** (alphabetical): for every doc_type with real
 *      document uploads. Layout inside, NEWEST → OLDEST:
 *
 *        ✒️ Signed [Bundle]  ← signed entries that include this doc_type
 *          └── 📄 v_signed (source version)
 *                └── 📎 attachments
 *        📄 v_latest
 *          └── 📎 attachments
 *        📄 v_older
 *          └── 📎 attachments
 *
 *      Versions chain via `parent_document_id`. Attachments hang under the
 *      matching version (parent_doc_type == type AND related_to_version == v).
 *
 *  Signed bundles are placed inside EVERY source doc_type collection they
 *  cover (resolved via source_document_id → bundle.source_document_ids → doc.doc_type).
 */
function buildGroups(documents) {
  const docs = Array.isArray(documents) ? documents : [];
  if (!docs.length) return [];

  const byId = new Map(docs.map((d) => [d.id, d]));

  // Drop staging/unsigned bundles entirely
  const visible = docs.filter((d) => !isUnsignedStagingBundle(d));

  // Buckets
  const signed = visible.filter((d) => isSignedDoc(d));
  const attachments = visible.filter((d) => d.is_thread_attachment);
  const regular = visible.filter((d) => (
    !isSignedDoc(d) &&
    !d.is_thread_attachment &&
    !isBundleDocType(d.doc_type)
  ));

  // Resolve a signed doc → its set of source DOC IDs (we no longer need types because we
  // don't surface signed docs in their own collection; only the chip lives in per-type rows)
  const sourceIdsOfSigned = (s) => {
    if (isSignedBundle(s)) {
      const origBundleId = s.source_document_id;
      const origBundle = origBundleId ? byId.get(origBundleId) : null;
      return origBundle?.source_document_ids || s.source_document_ids || [];
    }
    return s.source_document_id ? [s.source_document_id] : [];
  };

  // "what signed me?" index
  const signedByDocId = new Map();
  for (const s of signed) {
    for (const sid of sourceIdsOfSigned(s)) {
      const arr = signedByDocId.get(sid) || [];
      arr.push({ signed: s, bundle: isSignedBundle(s) });
      signedByDocId.set(sid, arr);
    }
  }

  // ─── BUNDLE CHAINS (iter110) ─────────────────────────────────────────────────────────
  // Generated bundles sharing the same doc_type in the same request form a single
  // versioned chain. Each chain renders as one card in the Bundle Hero with the LATEST
  // version as the header + expand chevron showing older versions + attachments.
  const isGeneratedBundleDoc = (d) =>
    Array.isArray(d?.source_document_ids) && d.source_document_ids.length > 0
    && !isSignedDoc(d);
  const allBundleDocs = visible.filter(isGeneratedBundleDoc);
  const bundleDocIds = new Set(allBundleDocs.map((d) => d.id));
  // Build the set of bundle THREAD IDs (chain shares one thread). Thread attachments
  // are linked to threads via `thread_id`, not `parent_document_id` — so we resolve
  // them by matching the chain's thread_id.
  const bundleThreadIds = new Set(allBundleDocs.map((d) => d.thread_id).filter(Boolean));
  // Map each thread attachment to its chain via thread_id; we'll then pick which
  // version of the chain it belongs to using `related_to_version` (set at upload time).
  const bundleAttachmentsByThread = new Map();
  for (const a of attachments) {
    if (a.thread_id && bundleThreadIds.has(a.thread_id)) {
      const arr = bundleAttachmentsByThread.get(a.thread_id) || [];
      arr.push(a);
      bundleAttachmentsByThread.set(a.thread_id, arr);
    }
  }
  // Group bundles by doc_type → chains
  const chainsByType = new Map();
  for (const b of allBundleDocs) {
    const dt = b.doc_type || 'Bundle';
    if (!chainsByType.has(dt)) chainsByType.set(dt, []);
    chainsByType.get(dt).push(b);
  }
  // Sort each chain by version DESC; head = highest version
  const bundleChainEntries = [];
  for (const [docType, chain] of chainsByType.entries()) {
    chain.sort((a, b) => (b.version || 1) - (a.version || 1)
                     || new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
    const head = chain[0];
    // All attachments live on the chain's shared thread. Bucket them by
    // `related_to_version` so each version row shows its own attachments; anything
    // unassigned defaults to the latest version.
    const threadId = head.thread_id;
    const threadAttachments = bundleAttachmentsByThread.get(threadId) || [];
    const attachmentsByVersion = new Map();
    for (const a of threadAttachments) {
      const v = a.related_to_version || head.version || 1;
      const versionDoc = chain.find((d) => (d.version || 1) === v) || head;
      const arr = attachmentsByVersion.get(versionDoc.id) || [];
      arr.push(a);
      attachmentsByVersion.set(versionDoc.id, arr);
    }
    const totalAttachments = threadAttachments.length;
    bundleChainEntries.push({
      kind: 'bundle-chain',
      docType,
      head: { ...head, _signedBy: signedByDocId.get(head.id) || [] },
      versions: chain, // newest-first
      attachmentsByVersion,
      versionCount: chain.length,
      attachmentCount: totalAttachments,
    });
  }

  // Drop bundles from the "regular per-doc-type" buckets
  const regularNonBundle = regular.filter((d) => !bundleDocIds.has(d.id));

  // ─── Per-source-type collections ONLY (iter102: top "Signed Documents" removed) ─────
  const groups = [];

  // Hero "Bundles" group goes first if any chain exists.
  if (bundleChainEntries.length > 0) {
    bundleChainEntries.sort((a, b) =>
      new Date(b.head.uploaded_at || 0) - new Date(a.head.uploaded_at || 0));
    groups.push({
      key: '__bundles__',
      label: 'Bundles',
      tone: 'bundle',
      icon: 'bundle',
      items: bundleChainEntries,
      _counts: {
        docs: bundleChainEntries.length,
        versions: allBundleDocs.length,
        attachments: bundleChainEntries.reduce((s, c) => s + c.attachmentCount, 0),
      },
    });
  }

  const byType = new Map();
  for (const d of regularNonBundle) {
    const t = d.doc_type || 'Other';
    // Defence-in-depth: skip phantom Signed_<...> doc_types if any slipped through.
    if ((t || '').startsWith('Signed_')) continue;
    if (isBundleDocType(t)) continue;
    if (!byType.has(t)) byType.set(t, []);
    byType.get(t).push(d);
  }

  const sortedTypes = [...byType.keys()].sort((a, b) => a.localeCompare(b));
  for (const t of sortedTypes) {
    const arr = byType.get(t) || [];

    const inTypeIds = new Set(arr.map((d) => d.id));

    // iter118 — Detect proper LINEAR chains only. A parent is "absorbed" (rendered
    // as an older version under its child's chain) ONLY when it has exactly one
    // direct child within the same type. If multiple docs point to the same parent
    // (branched history) or a doc has no parent, that doc stays as its own
    // top-level head with its own attachments grouped under it.
    const referencesByParent = new Map();
    for (const d of arr) {
      const pid = d.parent_document_id;
      if (pid && inTypeIds.has(pid)) {
        if (!referencesByParent.has(pid)) referencesByParent.set(pid, []);
        referencesByParent.get(pid).push(d.id);
      }
    }
    const absorbedIds = new Set();
    for (const [parentId, kidIds] of referencesByParent) {
      if (kidIds.length === 1) absorbedIds.add(parentId);
    }
    const heads = arr.filter((d) => !absorbedIds.has(d.id));

    const entries = heads.map((head) => {
      const chain = [head];
      let cursor = head.parent_document_id;
      const seen = new Set([head.id]);
      // Only walk INTO absorbed parents — heads on the branching tree stay separate.
      while (cursor && !seen.has(cursor) && absorbedIds.has(cursor)) {
        const prev = arr.find((x) => x.id === cursor);
        if (!prev) break;
        chain.push(prev);
        seen.add(prev.id);
        cursor = prev.parent_document_id;
      }
      chain.sort((a, b) => (b.version || 0) - (a.version || 0)
                       || new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
      return { kind: 'version', head: chain[0], versions: chain.slice(1), chain };
    }).sort((a, b) => {
      // Generated bundles (docs with source_document_ids) bubble to the top of the
      // collection so reviewers see them first.
      const aBundle = Array.isArray(a.head?.source_document_ids) && a.head.source_document_ids.length > 0;
      const bBundle = Array.isArray(b.head?.source_document_ids) && b.head.source_document_ids.length > 0;
      if (aBundle !== bBundle) return aBundle ? -1 : 1;
      // iter118 — when multiple heads exist in the same type (branched history),
      // sort latest version first so reviewers see the newest entries on top.
      const dv = (b.head.version || 0) - (a.head.version || 0);
      if (dv !== 0) return dv;
      return new Date(b.head.uploaded_at || 0) - new Date(a.head.uploaded_at || 0);
    });

    const attsForType = attachments.filter((a) => a.parent_doc_type === t);
    const attachToVersion = (versionDoc, isHeadFallback) => ({
      ...versionDoc,
      _attachments: attsForType.filter((a) => (
        a.related_to_version == null
          ? isHeadFallback
          : a.related_to_version === versionDoc.version
      )).sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0)),
      _signedBy: signedByDocId.get(versionDoc.id) || [],
    });
    const enrichedEntries = entries.map((e) => ({
      ...e,
      head: attachToVersion(e.head, true),
      versions: e.versions.map((v) => attachToVersion(v, false)),
    }));

    // Compute counts. Per UX direction:
    //   • docs = every file shown in the collection (each version row + each attachment row)
    //   • versions = total version count across all chains in this type
    //   • attachments = thread attachments tied to this type
    const totalVersions = enrichedEntries.reduce((acc, e) => acc + 1 + (e.versions?.length || 0), 0);
    const totalAttachments = attsForType.length;
    const totalDocs = totalVersions + totalAttachments;

    groups.push({
      key: t,
      label: t,
      tone: 'slate',
      icon: 'doc',
      items: enrichedEntries,
      _counts: { docs: totalDocs, versions: totalVersions, attachments: totalAttachments },
    });
  }

  return groups;
}

/**
 * (Removed in iter101) buildSignedItem + SignedRow used to render the now-deprecated
 * expandable "signed bundle with nested source docs" inside per-type collections.
 * The Option 1 layout uses flat signed rows in the top collection + a small
 * "✒️ Signed" chip on the source version row instead.
 */

// ─── UI bits ───────────────────────────────────────────────────────────────────

/**
 * BundleHero (iter110) — standalone "Bundles" hero. Each bundle CHAIN (one per
 * doc_type) is rendered as a card whose header shows the LATEST version + chip row
 * (doc-type, source-count, version-count, attachment-count, signed). An expand chevron
 * reveals all older versions and each version's thread attachments, mimicking a regular
 * doc-type collection but scoped inside the card.
 */
function BundleHero({ group, documentsById, onPreview, onDownload, downloadingDoc, isAdmin, onDeleteClick, deletingDocId }) {
  const chains = group.items || [];
  const [openChains, setOpenChains] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  if (chains.length === 0) return null;
  const toggle = (key) => setOpenChains((p) => ({ ...p, [key]: !p[key] }));
  return (
    <div
      className="rounded-2xl border border-indigo-200 overflow-hidden shadow-sm bg-white"
      data-testid="bundle-hero"
    >
      <div className="relative px-5 py-4 bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 text-white overflow-hidden">
        <div className="absolute -top-12 -right-10 opacity-20 pointer-events-none">
          <Layers className="w-44 h-44" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
            <Files className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-base tracking-tight">Bundles</h3>
              <Badge className="bg-amber-400/20 text-amber-100 border border-amber-300/30 text-[10px] uppercase tracking-wider">
                Generated
              </Badge>
            </div>
            <p className="text-xs text-white/70 mt-0.5">
              {chains.length} {chains.length === 1 ? 'chain' : 'chains'}
              {' · '}{group._counts?.versions || 0} total versions
              {(group._counts?.attachments || 0) > 0 && ` · ${group._counts.attachments} attachments`}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 h-8 gap-1"
            onClick={() => setCollapsed((c) => !c)}
            data-testid="bundle-hero-collapse-btn"
            title={collapsed ? 'Expand bundles' : 'Collapse bundles'}
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4 rotate-90" />}
            <span className="text-xs">{collapsed ? 'Expand' : 'Collapse'}</span>
          </Button>
        </div>
      </div>

      {!collapsed && (
      <div className="p-3 grid grid-cols-1 gap-3 bg-indigo-50/30">
        {chains.map((chain) => {
          const head = chain.head;
          const sourceCount = (head.source_document_ids || []).length;
          const signedBy = head._signedBy || [];
          const isOpen = !!openChains[chain.docType];
          const olderVersions = chain.versions.slice(1); // exclude head
          return (
            <div
              key={chain.docType}
              className="group rounded-xl border border-indigo-200 bg-white shadow-sm hover:shadow-md hover:border-indigo-400 transition-all duration-150"
              data-testid={`bundle-chain-${chain.docType}`}
            >
              {/* CHAIN HEAD ROW — latest version metadata */}
              <div className="flex items-start gap-3 p-3">
                <button
                  type="button"
                  onClick={() => toggle(chain.docType)}
                  className="mt-1 text-slate-400 hover:text-indigo-700 transition-colors"
                  data-testid={`bundle-chain-toggle-${chain.docType}`}
                  aria-label={isOpen ? 'Collapse chain' : 'Expand chain'}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shrink-0 shadow-sm">
                  <Layers className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="font-medium text-slate-900 truncate"
                    title={head.filename}
                    data-testid={`bundle-chain-head-filename-${chain.docType}`}
                  >
                    {head.filename}
                  </p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    <Badge variant="outline" className="text-[10px] border-indigo-300 bg-indigo-50 text-indigo-700">
                      {chain.docType}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-[10px] border-slate-300 bg-slate-50 text-slate-700 gap-1"
                      data-testid={`bundle-source-count-${head.id}`}
                    >
                      <FileText className="w-3 h-3" />
                      {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-[10px] border-indigo-300 bg-indigo-50 text-indigo-800 gap-1"
                      data-testid={`bundle-version-count-${chain.docType}`}
                    >
                      <Layers className="w-3 h-3" />
                      {chain.versionCount} {chain.versionCount === 1 ? 'version' : 'versions'}
                    </Badge>
                    {chain.attachmentCount > 0 && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-300 bg-amber-50 text-amber-700 gap-1"
                        data-testid={`bundle-attachment-count-${chain.docType}`}
                      >
                        <Paperclip className="w-3 h-3" />
                        {chain.attachmentCount} {chain.attachmentCount === 1 ? 'attachment' : 'attachments'}
                      </Badge>
                    )}
                    {signedBy.length > 0 && (
                      <Badge
                        className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-300 gap-1"
                        data-testid={`bundle-signed-chip-${head.id}`}
                      >
                        <BadgeCheck className="w-3 h-3" />Signed
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    v{head.version || 1} · {formatUploaderMeta(head.uploader_name, head.uploader_role)} · {formatDate(head.uploaded_at)}
                  </p>

                  {/* Sources preview on the head row only */}
                  {sourceCount > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Contains</p>
                      <div className="space-y-0.5">
                        {(head.source_document_ids || []).slice(0, 4).map((sid) => {
                          const s = documentsById[sid];
                          if (!s) return null;
                          return (
                            <button
                              key={sid}
                              type="button"
                              onClick={() => onPreview(s)}
                              className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-indigo-700 hover:underline truncate text-left w-full"
                              title={s.filename}
                              data-testid={`bundle-source-link-${head.id}-${sid}`}
                            >
                              <FileText className="w-3 h-3 shrink-0 text-slate-400" />
                              <span className="truncate">{s.filename}</span>
                            </button>
                          );
                        })}
                        {sourceCount > 4 && (
                          <p className="text-[11px] text-slate-400 italic">+{sourceCount - 4} more …</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Head action row */}
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => onPreview(head)}
                      data-testid={`bundle-preview-${head.id}`}
                    >
                      <Eye className="w-3 h-3" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => onDownload(head)}
                      disabled={downloadingDoc === head.id}
                      data-testid={`bundle-download-${head.id}`}
                    >
                      {downloadingDoc === head.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Download className="w-3 h-3" />}
                      Download
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-red-600 hover:bg-red-50 ml-auto"
                        onClick={() => onDeleteClick(head)}
                        disabled={deletingDocId === head.id}
                        data-testid={`bundle-delete-${head.id}`}
                      >
                        {deletingDocId === head.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* EXPANDED — older versions + each version's attachments */}
              {isOpen && (
                <div className="border-t border-indigo-100 bg-indigo-50/50 px-3 py-2 space-y-1.5" data-testid={`bundle-chain-body-${chain.docType}`}>
                  {chain.versions.map((v, idx) => {
                    const isHead = idx === 0;
                    const versionAtts = chain.attachmentsByVersion.get(v.id) || [];
                    return (
                      <div key={v.id} className="ml-7">
                        <div className="flex items-center gap-2 py-1.5 group/row">
                          <FileText className={`w-3.5 h-3.5 shrink-0 ${isHead ? 'text-indigo-600' : 'text-slate-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs truncate ${isHead ? 'font-medium text-slate-800' : 'text-slate-700'}`} title={v.filename}>
                              {v.filename}
                              {isHead && <Badge className="ml-1.5 text-[9px] bg-indigo-100 text-indigo-700 border border-indigo-300">Latest</Badge>}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              v{v.version || 1} · {formatUploaderMeta(v.uploader_name, v.uploader_role)} · {formatDate(v.uploaded_at)}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] gap-1 opacity-70 hover:opacity-100"
                            onClick={() => onPreview(v)}
                            data-testid={`bundle-version-preview-${v.id}`}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] gap-1 opacity-70 hover:opacity-100"
                            onClick={() => onDownload(v)}
                            disabled={downloadingDoc === v.id}
                            data-testid={`bundle-version-download-${v.id}`}
                          >
                            {downloadingDoc === v.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Download className="w-3 h-3" />}
                          </Button>
                        </div>
                        {/* Attachments under this version */}
                        {versionAtts.map((a) => (
                          <div key={a.id} className="ml-6 flex items-center gap-2 py-1 group/att">
                            <Paperclip className="w-3 h-3 text-amber-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] text-slate-700 truncate" title={a.filename}>{a.filename}</p>
                              <p className="text-[10px] text-slate-500">
                                Attachment · {formatUploaderMeta(a.uploader_name, a.uploader_role)} · {formatDate(a.uploaded_at)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-[10px] gap-1 opacity-70 hover:opacity-100"
                              onClick={() => onPreview(a)}
                              data-testid={`bundle-attachment-preview-${a.id}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function DocCollection({ group, isOpen, onToggle, openItems, onItemToggle, onPreview, onDownload, downloadingDoc, isAdmin, onDeleteClick, deletingDocId }) {
  const c = group._counts || { docs: group.items.length, versions: 0, attachments: 0 };
  // Collection has at least one signed version inside?
  const hasSignedInside = (group.items || []).some((entry) => (
    (entry.head?._signedBy?.length || 0) > 0 ||
    (entry.versions || []).some((v) => (v._signedBy?.length || 0) > 0)
  ));
  const toneCls = group.tone === 'emerald'
    ? 'border-emerald-200 bg-emerald-50/40'
    : 'border-slate-200 bg-white';
  const headerCls = group.tone === 'emerald' ? 'text-emerald-800' : 'text-slate-800';
  return (
    <div className={`rounded-xl border ${toneCls} overflow-hidden`} data-testid={`doc-collection-${group.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-black/5 transition-colors"
        data-testid={`doc-collection-toggle-${group.key}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <CollectionIcon icon={group.icon} />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <p className={`font-semibold ${headerCls}`}>{group.label}</p>
              {hasSignedInside && (
                <Badge
                  className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium"
                  data-testid={`collection-signed-chip-${group.key}`}
                >
                  <BadgeCheck className="w-3 h-3 mr-1" />Signed
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500" data-testid={`doc-collection-counts-${group.key}`}>
              {c.docs} {c.docs === 1 ? 'doc' : 'docs'}
              {' · '}{c.versions} {c.versions === 1 ? 'version' : 'versions'}
              {' · '}{c.attachments} {c.attachments === 1 ? 'attachment' : 'attachments'}
            </p>
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-1 space-y-2 border-t border-slate-200/60 bg-white" data-testid={`doc-collection-body-${group.key}`}>
          {group.items.map((item, idx) => {
            const key = item.head.id + '_' + idx + '_' + group.key;
            return (
              <CollectionItem
                key={key}
                item={item}
                isOpen={!!openItems[key]}
                onToggle={() => onItemToggle(key)}
                onPreview={onPreview}
                onDownload={onDownload}
                downloadingDoc={downloadingDoc}
                isAdmin={isAdmin}
                onDeleteClick={onDeleteClick}
                deletingDocId={deletingDocId}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function CollectionIcon({ icon }) {
  if (icon === 'signed') return <BadgeCheck className="w-5 h-5 text-emerald-600" />;
  return <FileText className="w-5 h-5 text-slate-500" />;
}

function CollectionItem({ item, isOpen, onToggle, onPreview, onDownload, downloadingDoc, isAdmin, onDeleteClick, deletingDocId }) {
  return (
    <VersionRow
      entry={item}
      isOpen={isOpen}
      onToggle={onToggle}
      onPreview={onPreview}
      onDownload={onDownload}
      downloadingDoc={downloadingDoc}
      isAdmin={isAdmin}
      onDeleteClick={onDeleteClick}
      deletingDocId={deletingDocId}
    />
  );
}

function VersionRow({ entry, isOpen, onToggle, onPreview, onDownload, downloadingDoc, isAdmin, onDeleteClick, deletingDocId }) {
  const head = entry.head;
  const headAtts = head._attachments || [];
  const headSignedBy = head._signedBy || [];
  const hasOlder = (entry.versions?.length || 0) > 0;
  const hasChildren = hasOlder || headAtts.length > 0
    || (entry.versions || []).some((v) => (v._attachments || []).length > 0);
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`doc-item-${head.id}`}>
      <div className="flex items-center gap-3 p-3">
        <FileText className="w-6 h-6 text-blue-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-900 truncate" title={head.filename}>{head.filename}</p>
            {Array.isArray(head.source_document_ids) && head.source_document_ids.length > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-blue-300 bg-blue-50 text-blue-700"
                data-testid={`doc-bundle-chip-${head.id}`}
              >
                <Layers className="w-3 h-3" />
                Bundle
              </Badge>
            )}
            {headSignedBy.map((s, i) => (
              <SignedChip
                key={s.signed.id + '_' + i}
                signedDoc={s.signed}
                bundle={s.bundle}
                onPreview={onPreview}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500">
            v{head.version} · {formatUploaderMeta(head.uploader_name, head.uploader_role)} · {formatDate(head.uploaded_at)}
          </p>
        </div>
        <StatusPill status={head.status} />
        <RowButtons
          doc={head}
          onPreview={onPreview}
          onDownload={onDownload}
          downloadingDoc={downloadingDoc}
          isAdmin={isAdmin}
          onDeleteClick={onDeleteClick}
          deletingDocId={deletingDocId}
        />
        {hasChildren && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="ml-1"
            data-testid={`doc-item-toggle-${head.id}`}
            title={isOpen ? 'Hide details' : 'Show versions & attachments'}
          >
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        )}
      </div>

      {hasChildren && isOpen && (
        <div className="border-t border-slate-100 px-3 pb-3 pt-2 space-y-1.5" data-testid={`doc-item-children-${head.id}`}>
          {headAtts.map((a) => (
            <ChildRow
              key={a.id}
              icon={<Paperclip className="w-4 h-4 text-amber-500" />}
              label={a.filename}
              meta={`Attachment · ${formatUploaderMeta(a.uploader_name, a.uploader_role)} · ${formatDate(a.uploaded_at)}`}
              onPreview={() => onPreview(a)}
              onDownload={() => onDownload(a)}
              downloading={downloadingDoc === a.id}
              testId={`doc-attachment-${a.id}`}
            />
          ))}
          {entry.versions.map((v) => (
            <div key={v.id}>
              <ChildRow
                icon={<FileText className="w-4 h-4 text-slate-400" />}
                label={v.filename}
                meta={`v${v.version} · ${formatUploaderMeta(v.uploader_name, v.uploader_role)} · ${formatDate(v.uploaded_at)}`}
                onPreview={() => onPreview(v)}
                onDownload={() => onDownload(v)}
                downloading={downloadingDoc === v.id}
                testId={`doc-version-${v.id}`}
                trailingChips={(v._signedBy || []).map((s, i) => (
                  <SignedChip
                    key={s.signed.id + '_' + i}
                    signedDoc={s.signed}
                    bundle={s.bundle}
                    onPreview={onPreview}
                  />
                ))}
              />
              {(v._attachments || []).map((a) => (
                <ChildRow
                  key={a.id}
                  indent={2}
                  icon={<Paperclip className="w-4 h-4 text-amber-500" />}
                  label={a.filename}
                  meta={`Attachment · ${formatUploaderMeta(a.uploader_name, a.uploader_role)} · ${formatDate(a.uploaded_at)}`}
                  onPreview={() => onPreview(a)}
                  onDownload={() => onDownload(a)}
                  downloading={downloadingDoc === a.id}
                  testId={`doc-attachment-${a.id}`}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Small clickable emerald pill that announces "this version was signed" and opens the signed PDF preview. */
function SignedChip({ signedDoc, bundle, onPreview }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPreview(signedDoc); }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300 text-xs font-medium transition-colors"
      data-testid={`signed-chip-${signedDoc.id}`}
      title={`Click to open ${signedDoc.filename}`}
    >
      <BadgeCheck className="w-3 h-3" />
      {bundle ? 'Bundled & Signed' : 'Signed'}
    </button>
  );
}

function RowButtons({ doc, onPreview, onDownload, downloadingDoc, isAdmin, onDeleteClick, deletingDocId }) {
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPreview(doc)}
        data-testid={`doc-preview-${doc.id}`}
        title="Preview"
      >
        <Eye className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDownload(doc)}
        disabled={downloadingDoc === doc.id}
        data-testid={`download-doc-${doc.id}`}
        title="Download"
      >
        {downloadingDoc === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </Button>
      {isAdmin && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDeleteClick(doc)}
          disabled={deletingDocId === doc.id}
          className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
          data-testid={`delete-doc-${doc.id}`}
          title="Delete (Admin)"
        >
          {deletingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </Button>
      )}
    </>
  );
}

function ChildRow({ icon, label, meta, onPreview, onDownload, downloading, testId, indent = 1, trailingChips = null }) {
  const padLeft = indent === 2 ? 'pl-12' : 'pl-6';
  return (
    <div className={`flex items-center gap-3 ${padLeft} py-1.5 hover:bg-slate-50 rounded`} data-testid={testId}>
      {icon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-slate-800 truncate" title={label}>{label}</p>
          {trailingChips}
        </div>
        <p className="text-xs text-slate-500">{meta}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onPreview} title="Preview"><Eye className="w-4 h-4" /></Button>
      <Button variant="ghost" size="sm" onClick={onDownload} disabled={downloading} title="Download">
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </Button>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const cls = status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700'
            : status === 'REJECTED' ? 'bg-red-100 text-red-700'
            : 'bg-amber-100 text-amber-800';
  const label = status === 'PENDING_REVIEW' ? 'Pending' : status.charAt(0) + status.slice(1).toLowerCase();
  return <Badge className={cls}>{label}</Badge>;
}

export default DocumentsTab;
